import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import TrainingPlanTable, { DEFAULT_PLAN_ROWS } from './TrainingPlanTable';
import ConfirmModal from './ConfirmModal';
import './TrainerTemplates.css';

const API_URL = '/api';

function TrainerTemplates({ auth }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { templateId } = useParams();

  const isCreatingNew = location.pathname.endsWith('/templates/new');
  const isEditing = Boolean(templateId) || isCreatingNew;

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [templateToDelete, setTemplateToDelete] = useState(null);

  // Active template state when editing/creating
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(false);

  // Fetch all templates
  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`${API_URL}/trainer/templates`, {
        headers: { Authorization: `Bearer ${auth?.token}` }
      });
      setTemplates(res.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Error loading templates');
    } finally {
      setLoading(false);
    }
  }, [auth?.token]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Load single template if editing existing
  useEffect(() => {
    if (isCreatingNew) {
      setActiveTemplate({
        id: null,
        name: '',
        rows: DEFAULT_PLAN_ROWS
      });
      setTemplateLoading(false);
    } else if (templateId) {
      const existing = templates.find(t => String(t.id) === String(templateId));
      if (existing) {
        let rows = DEFAULT_PLAN_ROWS;
        if (existing.content) {
          if (Array.isArray(existing.content)) {
            rows = existing.content;
          } else if (existing.content.rows && Array.isArray(existing.content.rows)) {
            rows = existing.content.rows;
          }
        }
        setActiveTemplate({
          id: existing.id,
          name: existing.name || '',
          rows: rows.length > 0 ? rows : DEFAULT_PLAN_ROWS
        });
      } else {
        // Fetch from server
        setTemplateLoading(true);
        axios.get(`${API_URL}/trainer/templates/${templateId}`, {
          headers: { Authorization: `Bearer ${auth?.token}` }
        })
        .then(res => {
          const t = res.data;
          let rows = DEFAULT_PLAN_ROWS;
          if (t.content) {
            if (Array.isArray(t.content)) {
              rows = t.content;
            } else if (t.content.rows && Array.isArray(t.content.rows)) {
              rows = t.content.rows;
            }
          }
          setActiveTemplate({
            id: t.id,
            name: t.name || '',
            rows: rows.length > 0 ? rows : DEFAULT_PLAN_ROWS
          });
        })
        .catch(err => {
          setError(err.response?.data?.error || 'Template not found');
        })
        .finally(() => {
          setTemplateLoading(false);
        });
      }
    } else {
      setActiveTemplate(null);
    }
  }, [templateId, isCreatingNew, templates, auth?.token]);

  // Handle saving template (manual click)
  const handleSaveTemplate = async (templateData, isManualSave = true) => {
    const name = (templateData.name || '').trim();
    if (!name) {
      return;
    }

    const payload = {
      name,
      content: {
        rows: templateData.rows || []
      }
    };

    try {
      if (activeTemplate?.id) {
        // Update existing template
        const res = await axios.put(`${API_URL}/trainer/templates/${activeTemplate.id}`, payload, {
          headers: { Authorization: `Bearer ${auth?.token}` }
        });
        setTemplates(prev => prev.map(t => (t.id === activeTemplate.id ? res.data : t)));
        if (isManualSave) {
          setSuccessMsg(`Template "${name}" saved successfully.`);
          setTimeout(() => setSuccessMsg(''), 3000);
          navigate('/trainer/templates');
        }
      } else {
        // Create new template
        const res = await axios.post(`${API_URL}/trainer/templates`, payload, {
          headers: { Authorization: `Bearer ${auth?.token}` }
        });
        setTemplates(prev => [res.data, ...prev]);
        if (isManualSave) {
          setSuccessMsg(`Template "${name}" created successfully.`);
          setTimeout(() => setSuccessMsg(''), 3000);
          navigate('/trainer/templates');
        } else {
          setActiveTemplate({
            id: res.data.id,
            name: res.data.name,
            rows: templateData.rows || []
          });
          // Transition URL smoothly to the new template ID
          navigate(`/trainer/templates/${res.data.id}`, { replace: true });
        }
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Error saving template';
      setError(msg);
    }
  };

  // Handle deleting template
  const handleDeleteTemplate = async (idToDelete) => {
    const targetId = idToDelete || activeTemplate?.id;
    if (!targetId) {
      navigate('/trainer/templates');
      return;
    }

    try {
      await axios.delete(`${API_URL}/trainer/templates/${targetId}`, {
        headers: { Authorization: `Bearer ${auth?.token}` }
      });
      setTemplates(prev => prev.filter(t => t.id !== targetId));
      setSuccessMsg('Template deleted successfully.');
      setTimeout(() => setSuccessMsg(''), 3000);
      navigate('/trainer/templates');
    } catch (err) {
      alert(err.response?.data?.error || 'Error deleting template');
    }
  };

  // If in editor mode
  if (isEditing) {
    if (templateLoading) {
      return <div className="loading">Loading template...</div>;
    }

    return (
      <div className="trainer-templates-container">
        {error && <div className="error">{error}</div>}
        {successMsg && <div className="success-banner">{successMsg}</div>}
        <TrainingPlanTable
          key={activeTemplate?.id || 'new-template'}
          initialData={activeTemplate}
          isTemplate={true}
          templateName={activeTemplate?.name || ''}
          onSaveTemplate={handleSaveTemplate}
          onDeletePlan={handleDeleteTemplate}
          onBack={() => navigate('/trainer/templates')}
          auth={auth}
          canEditPlan={true}
        />
      </div>
    );
  }

  // Templates List View
  return (
    <div className="trainer-templates-container">
      {error && <div className="error">{error}</div>}
      {successMsg && <div className="success-banner">{successMsg}</div>}

      <div className="templates-section-card">
        {/* Navigation Tabs and Add Button */}
        <div className="trainer-section-header">
          <div className="trainer-nav-tabs">
            <button
              type="button"
              className="trainer-nav-tab"
              onClick={() => navigate('/trainer')}
            >
              Clients
            </button>
            <button
              type="button"
              className="trainer-nav-tab active"
            >
              Templates
            </button>
          </div>

          <button
            type="button"
            className="button button-primary btn-add-template"
            onClick={() => navigate('/trainer/templates/new')}
          >
            New Template
          </button>
        </div>

        {/* Templates List Table */}
        {loading ? (
          <div className="loading">Loading templates...</div>
        ) : templates.length === 0 ? (
          <div className="empty-state">
            No templates created yet. Click <span>New Template</span> to create your first workout template.
          </div>
        ) : (
          <div className="table-responsive">
            <table className="trainer-templates-table">
              <tbody>
                {templates.map((template) => {
                  return (
                    <tr key={template.id} className="trainer-template-row">
                      <td className="cell-template-name">
                        <button
                          type="button"
                          className="btn-template-link"
                          onClick={() => navigate(`/trainer/templates/${template.id}`)}
                          title="Click to edit template"
                        >
                          {template.name}
                        </button>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="template-table-actions">
                          <button
                            type="button"
                            className="button button-secondary btn-sm btn-action-edit"
                            onClick={() => navigate(`/trainer/templates/${template.id}`)}
                            title="Edit template"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="button button-danger btn-sm btn-action-delete"
                            onClick={() => setTemplateToDelete(template)}
                            title="Delete template"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      <ConfirmModal
        isOpen={Boolean(templateToDelete)}
        message={templateToDelete ? `Are you sure you want to delete template ${templateToDelete.name}?` : ''}
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={() => {
          if (templateToDelete) {
            handleDeleteTemplate(templateToDelete.id);
            setTemplateToDelete(null);
          }
        }}
        onCancel={() => setTemplateToDelete(null)}
      />
    </div>
  );
}

export default TrainerTemplates;
